import React from 'react';
import { Col } from 'react-bootstrap';
import DatePickerInput from 'components/input/DatePicker';
import GroupRadioButton from 'components/input/GroupRadioButton';
import SelectInput from 'components/input/SelectInput';
import TextInput from 'components/input/textInput';
import GroupCheckbox from 'components/input/GroupCheckbox';
import { isEmptyOrNull, progressionHighSchool } from 'components/helper/helper';
import TextAreaInput from 'components/input/TextAreaInput';
import Grid from '@mui/joy/Grid';

interface Props {
  formikGraduatingSchool: any;
  highSchoolOptions: any[];
  check?: any;
  isMobile?: boolean;
  disabled?: boolean;
}

const GraduatingSchool = (props: Props) => {
  const { check, isMobile = false, disabled = false } = props;
  const [highSchoolOptions, setHighSchoolOptions] = React.useState<any[]>([]);
  React.useEffect(() => {
    const values = props.highSchoolOptions.map((item) => {
      return { value: item?.high_school_name || item?.fullName };
    });

    let list = values.filter((item: any) => item.value);
    list.push({
      value:
        'I did not finish high school but instead took a test (such as GED or CHSPE) to earn high school equivalency.',
    });
    setHighSchoolOptions(list);
  }, [props.highSchoolOptions]);

  const decile_rank_options = [
    { value: 'Top 10%' },
    { value: 'Top 20%' },
    { value: 'Top 30%' },
    { value: 'Top 40%' },
    { value: 'Top 50%' },
    { value: 'Top 60%' },
    { value: 'Top 70%' },
    { value: 'Top 80%' },
    { value: 'Top 90%' },
    { value: 'Top 100%' },
  ];

  const quintile_rank_options = [
    { value: 'Top 20%' },
    { value: 'Top 40%' },
    { value: 'Top 60%' },
    { value: 'Top 80%' },
    { value: 'Top 100%' },
    // Clear
  ];

  const quartile_rank_options = [
    { value: 'Top 25%' },
    { value: 'Top 50%' },
    { value: 'Top 75%' },
    { value: 'Top 100%' },
    // Clear
  ];

  return (
    <>
      <Col xs={12} className="text-dart fs-5 p-1 mt-3">
        Graduation Status
      </Col>
      <GroupRadioButton
        gridGap={'0px 32px'}
        mdCol={12}
        logo="span-csu-logo"
        name="graduation_status"
        values={props.formikGraduatingSchool.values.graduation_status}
        onChange={(e: any) => {
          props.formikGraduatingSchool.handleChange(e);
          if (e.target.value === 'I will not be receiving a high school diploma or equivalent') {
            props.formikGraduatingSchool.setFieldValue('graduating_date_received', null);
          }
        }}
        disabled={disabled}
        label="Select your anticipated or current graduation status from high school or equivalent.*"
        options={[
          {
            value: 'I have or will graduate with my high school diploma',
            extendElement: (
              <>
              <DatePickerInput
                logo="span-csu-logo"
                label="Date Received/Expected*"
                name="graduating_date_received"
                values={props.formikGraduatingSchool.values.graduating_date_received}
                onChange={(e) =>
                  props.formikGraduatingSchool.setFieldValue('graduating_date_received', e)
                }
                disabled={disabled}
                isMissing={!props.formikGraduatingSchool.values.graduating_date_received && check}
              />
              </>
            ),
          },
          {
            value: 'I have or will have a high school equivalency',
            extendElement: (
              <>
              <DatePickerInput
                logo="span-csu-logo"
                label="Date Received/Expected*"
                name="graduating_date_received"
                values={props.formikGraduatingSchool.values.graduating_date_received}
                onChange={(e) =>
                  props.formikGraduatingSchool.setFieldValue('graduating_date_received', e)
                }
                isMissing={!props.formikGraduatingSchool.values.graduating_date_received && check}
                disabled={disabled}
              />
              </>
            ),
          },
          { value: 'I will not be receiving a high school diploma or equivalent' },
        ]}
        isMissing={!props.formikGraduatingSchool.values.graduation_status && check}
      />

      <GroupCheckbox
        gridGap={'0 32px'}
        label="Please indicate if any of these options will have affected your progression through or
                since secondary/high school. Check all that apply.*"
        logo="span-cm-logo"
        options={progressionHighSchool}
        form={props.formikGraduatingSchool}
        mdCol={6}
        isMissing={
          check &&
          !props.formikGraduatingSchool.values.progression_is_graduate_early &&
          !props.formikGraduatingSchool.values.progression_is_graduate_late &&
          !props.formikGraduatingSchool.values.progression_is_time_off &&
          !props.formikGraduatingSchool.values.progression_is_take_gap_year &&
          !props.formikGraduatingSchool.values.progression_is_no_change
        }
        disabled={disabled}
      />

      {(props.formikGraduatingSchool.values.progression_is_graduate_early ||
        props.formikGraduatingSchool.values.progression_is_graduate_late ||
        props.formikGraduatingSchool.values.progression_is_time_off ||
        props.formikGraduatingSchool.values.progression_is_take_gap_year) && (
        <>
          <TextAreaInput
            isCMLogo={true}
            mdCol={'12'}
            logo="span-cm-logo"
            label="Please use the space below to provide details about the change in progression through secondary/high school that you indicated above.*"
            name="change_in_progression_through_highschool"
            values={props.formikGraduatingSchool.values.change_in_progression_through_highschool}
            onChange={props.formikGraduatingSchool.handleChange}
            countWord={250}
            isMissing={!props.formikGraduatingSchool.values.change_in_progression_through_highschool && check}
            disabled={disabled}
          />
        </>
      )}

      {props.formikGraduatingSchool.values.graduation_status ===
        'I have or will graduate with my high school diploma' && (
        <>
          <Col xs={12}>Graduating School</Col>
          <GroupRadioButton
            className="my-16"
            gridGap={'0px 32px'}
            logo="span-csu-logo"
            label="Please select your graduating high school.*"
            mdCol={12}
            options={highSchoolOptions}
            name="graduating_high_school"
            values={props.formikGraduatingSchool.values.graduating_high_school}
            onChange={props.formikGraduatingSchool.handleChange}
            isMissing={!props.formikGraduatingSchool.values.graduating_high_school && check}
            disabled={disabled}
          />
        </>
      )}

      <Grid container spacing={2} className="gm-form-fields my-16">
        <Grid xs={12} md={6}>
          <SelectInput
            option={['9th', '10th', '11th', '12th', 'Post-12th Year', 'Already Graduated']}
            className="uc-common-flag"
            label="Most recent academic year *"
            name="graduating_school_year"
            values={props.formikGraduatingSchool.values.graduating_school_year}
            onChange={props.formikGraduatingSchool.handleChange}
            isMissing={!props.formikGraduatingSchool.values.graduating_school_year && check}
            disabled={disabled}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <SelectInput
            logo="span-cm-logo"
            form={props.formikGraduatingSchool}
            label="GPA Weighting*"
            isSatTest={true}
            option={[{ value: 'Weighted' }, { value: 'Unweighted' }]}
            name="graduating_school_gpa_type"
            values={props.formikGraduatingSchool.values.graduating_school_gpa_type}
            onChange={props.formikGraduatingSchool.handleChange}
            isMissing={!props.formikGraduatingSchool.values.graduating_school_gpa_type && check}
            disabled={disabled}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <SelectInput
            logo="span-cm-logo"
            form={props.formikGraduatingSchool}
            label="GPA Scale*"
            isSatTest={true}
            option={[
              '4',
              '5',
              '100',
              'Other',
            ]}
            name="graduating_school_gpa_scale"
            values={props.formikGraduatingSchool.values.graduating_school_gpa_scale}
            onChange={props.formikGraduatingSchool.handleChange}
            isMissing={!props.formikGraduatingSchool.values.graduating_school_gpa_scale && check}
            disabled={disabled}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <TextInput
            type="number"
            decimalStep={0.001}
            max={99999}
            logo="span-cm-logo"
            label="Cumulative GPA*"
            isTestCsuLogo={true}
            name="graduating_school_cumulative_gpa"
            values={props.formikGraduatingSchool.values.graduating_school_cumulative_gpa}
            onChange={props.formikGraduatingSchool.handleChange}
            isMissing={!props.formikGraduatingSchool.values.graduating_school_cumulative_gpa && check}
            disabled={disabled}
          />
        </Grid>
        
        <Grid xs={12}>
          <SelectInput
            logo="span-cm-logo"
            form={props.formikGraduatingSchool}
            label="Class rank reporting*"
            isSatTest={true}
            isMissing={!props.formikGraduatingSchool.values.graduating_school_rank_report && check}
            option={[
              { value: 'Exact' },
              { value: 'Decile' },
              { value: 'Quintile' },
              { value: 'Quartile' },
              { value: 'None' },
            ]}
            name="graduating_school_rank_report"
            values={props.formikGraduatingSchool.values.graduating_school_rank_report}
            onChange={(e) => {
              props.formikGraduatingSchool.handleChange(e);
              switch(e.target.value) {
                case 'Exact':
                  props.formikGraduatingSchool.setFieldValue('class_rank', 0);
                  break;
                case 'Decile':
                  props.formikGraduatingSchool.setFieldValue('decile_rank', decile_rank_options[0].value);
                  break;
                case 'Quintile':
                  props.formikGraduatingSchool.setFieldValue('quintile_rank', quintile_rank_options[0].value);
                  break;
                case 'Quartile':
                  props.formikGraduatingSchool.setFieldValue('quartile_rank', quartile_rank_options[0].value);
                  break;
                case 'None':
                default:
                  props.formikGraduatingSchool.setFieldValue('class_rank', null);
                  props.formikGraduatingSchool.setFieldValue('decile_rank',  '');
                  props.formikGraduatingSchool.setFieldValue('quintile_rank', '');
                  props.formikGraduatingSchool.setFieldValue('quartile_rank', '');
                  break;
              }
              
            }}
            feedback={<>Only report if taken directly from your transcript</>}
            disabled={disabled}
          />
        </Grid>


        {props.formikGraduatingSchool.values.graduating_school_rank_report === 'Exact' && (
          <Grid xs={12}>
            <TextInput
              className="hover-feedback"
              logo="span-cm-logo"
              isTestCsuLogo={true}
              label="Class Rank*"
              type="number"
              max={99999}
              min={0}
              name="class_rank"
              values={props.formikGraduatingSchool.values.class_rank}
              onChange={(e) => {
                let num = Number(e.target.value);
                if (num>=0) {
                  props.formikGraduatingSchool.setFieldValue('class_rank', num);
                }
                else 
                {
                  props.formikGraduatingSchool.setFieldValue('class_rank', '');
                }
              }}
              isMissing={isEmptyOrNull(props.formikGraduatingSchool.values.class_rank) && check}
              feedback={<><span className="gm-hidden-feedback">Value range: 0-99999</span><span> &nbsp;</span></>}
            />
          </Grid>
        )}


        {props.formikGraduatingSchool.values.graduating_school_rank_report === 'Decile' && (
          <Grid xs={12}>
              <GroupRadioButton
                  columns={2}
                  logo="span-cm-logo"
                  label="Decile rank*"
                  name="decile_rank"
                  values={props.formikGraduatingSchool.values.decile_rank}
                  onChange={props.formikGraduatingSchool.handleChange}
                  options={decile_rank_options}
                  isMissing={isEmptyOrNull(props.formikGraduatingSchool.values.decile_rank) && check}
                  disabled={disabled}
              />
          </Grid>
        )}

        {props.formikGraduatingSchool.values.graduating_school_rank_report === 'Quintile' && (
          <Grid xs={12}>
              <GroupRadioButton
                  columns={2}
                  logo="span-cm-logo"
                  label="Quintile rank*"
                  name="quintile_rank"
                  onClear={() => props.formikGraduatingSchool.setFieldValue('quintile_rank', '')}
                  values={props.formikGraduatingSchool.values.quintile_rank}
                  onChange={props.formikGraduatingSchool.handleChange}
                  options={quintile_rank_options}
                  isMissing={isEmptyOrNull(props.formikGraduatingSchool.values.quintile_rank) && check}
                  disabled={disabled}
              />
          </Grid>
        )}

        {props.formikGraduatingSchool.values.graduating_school_rank_report === 'Quartile' && (
          <Grid xs={12}>
              <GroupRadioButton
                  columns={2}
                  logo="span-cm-logo"
                  label="Quartile rank*"
                  name="quartile_rank"
                  onClear={() => props.formikGraduatingSchool.setFieldValue('quartile_rank', '')}
                  values={props.formikGraduatingSchool.values.quartile_rank}
                  onChange={props.formikGraduatingSchool.handleChange}
                  options={quartile_rank_options}
                  isMissing={isEmptyOrNull(props.formikGraduatingSchool.values.quartile_rank) && check}
                  disabled={disabled}
              />
          </Grid>
        )}


        <Grid xs={12}>
          <GroupRadioButton
              label="How are the grades for this class reported on your transcript?*"
              logo="span-cm-logo"
              name="rank_weighting"
              onClear={() => props.formikGraduatingSchool.setFieldValue('rank_weighting', '')}
              values={props.formikGraduatingSchool.values.rank_weighting}
              onChange={props.formikGraduatingSchool.handleChange}
              options={[{ value: 'Weighted' }, { value: 'Unweighted' }]}
              isMissing={!props.formikGraduatingSchool.values.rank_weighting && check}
              disabled={disabled}
          />
        </Grid>
        
        <Grid xs={12}>
          <div>
          <TextInput
              className="hover-feedback"
              label="Graduating Class Size (approx.)*"
              logo="span-cm-logo"
              isTestCsuLogo={true}
              type="number"
              max={99999}
              min={0}
              name="graduating_class_size"
              values={props.formikGraduatingSchool.values.graduating_class_size}
              onChange={props.formikGraduatingSchool.handleChange}
              isMissing={!props.formikGraduatingSchool.values.graduating_class_size && check}
              disabled={disabled}
              feedback={<><span className="gm-hidden-feedback">Value range: 0-99999</span><span> &nbsp;</span></>}
          />
          </div>
        </Grid>

      </Grid>
    </>
  );
};

export default GraduatingSchool;
